import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, effect, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { VesselReportsStore } from '@navstation-portal/reporting/data-access/vessel-reports';
import {
  CarryOverRules,
  FieldRules,
  ReportFormRuleTemplate,
  RuleContext,
  RuleNode,
  TemplateLabel,
} from '@navstation-portal/reporting/data-model/ui';
import {
  BUNKER_TANK_FIELDS,
  FormState,
  REPORT_TABS,
  ReportTabRoute,
  ReportTabRulesKey,
  ReportTabStripRoute,
  ReportType,
  initialReportState,
  isReadOnlyReportStatus,
} from '@navstation-portal/reporting/data-model/vessel-report';
import {
  CarryOverExplanation,
  CarryOverWrite,
  MISSING_DISABLED,
  MISSING_READ_ONLY,
  MISSING_REQUIRED,
  MarkupFallback,
  NamedRuleExplanation,
  NamedValueExplanation,
  RuleExplanation,
  collectCarryOverWrites,
  evaluate,
  explainCarryOverValue,
  explainLabel,
  explainRule,
  explainValue,
  resolveCarryOverValue,
  resolveLabel,
  resolveValue,
} from '@navstation-portal/reporting/util/form-rules';
import { AuthStore } from '@navstation-portal/shared/data-access/auth';
import { VesselSpecStore } from '@navstation-portal/vessels/data-access/store';
import { signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { Dispatcher, Events } from '@ngrx/signals/events';
import { findBoundStatuses } from './bound-status.utility';
import { BunkerStore } from './bunker/bunker.store';
import { JudgedValue, findCalculatedValueStatuses } from './calculated-value-status.utility';
import { CargoDetailsStore } from './cargo-details/cargo-details.store';
import { ConsumptionsStore } from './consumptions/consumptions.store';
import {
  FieldWarnings,
  blockedExplanation,
  evaluateOr,
  explainFieldWarnings,
  findField,
  isFieldOnScreen,
  resolveFieldWarnings,
} from './form-rule-lookup.utility';
import { FORM_STORE_EVENTS } from './form-store-events.map';
import { OperationalStore } from './operational/operational.store';
import { PositionAndWeatherStore } from './position-and-weather/position-and-weather.store';
import { PowerStore } from './power/power.store';
import { getAvailableFuelGradeOptions, getAvailableFuelGrades, getTankOptions } from './report.helpers';
import { findStaleInvalidFields } from './stale-field-status.utility';
import { StockStore } from './stock/stock.store';
import { areTankFieldsValid } from './vm-builder-helpers';
import { CalculatedValue } from './with-calculated-values';
import { withCarryOverOnCreate } from './with-carry-over-on-create';
import { withFormRuleDebugEventHandlers } from './with-form-rule-debug-event-handlers';
import { AdminOverride, withFormRuleDebugReducer } from './with-form-rule-debug-reducer';
import { withReportEventHandlers } from './with-report-event-handlers';
import { withReportReducer } from './with-report-reducer';

// DEV-ONLY form-rule debug. Adds the subject block the debug tooltip shows.
// explainRule is pure: it knows a node and a context, and it knows no ids and
// no labels. This adds what the store knows. A label of null means the caller
// gave no fallback and the template holds none, so the tooltip shows no label
// line. Delete this function with the rest of the form-rule debug UI.
const withSubject = (
  explanation: RuleExplanation,
  path: string,
  templateLabel: TemplateLabel | undefined,
  fallbackLabel: string | undefined,
  context: RuleContext,
  otherRules?: NamedRuleExplanation[],
  valueRules?: NamedValueExplanation[],
  carryOver?: CarryOverExplanation
): RuleExplanation => {
  const resolved = resolveLabel(templateLabel, context);
  return {
    ...explanation,
    subject: {
      path,
      label: resolved ?? fallbackLabel ?? null,
      // A label tree that chose no text falls back, so the source follows the
      // resolved label, not the shape of the template entry.
      labelSource: resolved === undefined ? 'fallback' : typeof templateLabel === 'string' ? 'db' : 'db-rule',
      labelLines: explainLabel(templateLabel, context),
      otherRules,
      valueRules,
      carryOver,
    },
  };
};

// DEV-ONLY form-rule debug. The carry-over block of one field. Carry-over is
// the one rule that writes, so the block reports what the button would put in
// before anyone presses it, and why it would put nothing in.
//
// It shows on every field, where the bounds show on a number field and a date
// field alone: carry-over applies to every field. Delete this with the rest of
// the form-rule debug UI.
const explainCarryOver = (
  carryOver: CarryOverRules | undefined,
  context: RuleContext,
  locks: { readOnly: boolean; disabled: boolean }
): CarryOverExplanation => {
  // 'the template holds no entry' and 'the template says never' are different
  // facts about a field, and only the block tells them apart.
  if (carryOver === undefined) {
    return { modes: '(none)', detail: null, lines: [] };
  }

  const modes = [carryOver.onCreate ? 'onCreate' : null, carryOver.onRequest ? 'onRequest' : null].filter(
    (mode) => mode !== null
  );

  return {
    modes: modes.length === 0 ? 'never' : modes.join(', '),
    // A lock stops a button that draws, so the reason takes the place of the
    // value. A field with onRequest false draws no button, so no lock can stop
    // one, and the value is what a reader wants there.
    detail: carryOver.onRequest
      ? (lockReason(locks) ?? carryOverValue(carryOver, context))
      : carryOverValue(carryOver, context),
    lines: explainCarryOverValue(carryOver.value, context),
  };
};

/** Which lock stops the button, or null while neither does. */
const lockReason = (locks: { readOnly: boolean; disabled: boolean }): string | null => {
  // Each reason names the rule that holds the lock, so a reader goes straight
  // to the block above that answers for it.
  if (locks.disabled) return 'the disabled rule locks the field, so the button cannot run';
  if (locks.readOnly) return 'the read-only rule locks the field, so the button cannot run';
  return null;
};

/**
 * The value the carry-over would write, for a reader.
 *
 * A date reads as ISO, so it carries its time zone. A select option reads as
 * its name, which is the text the control shows, as the carry-over button
 * shows it on hover. Everything else reads as itself, because carry-over
 * writes what the control holds. A node that gives none reads '(none)', which
 * is the state of a button the user can press that would write nothing.
 */
const carryOverValue = (carryOver: CarryOverRules, context: RuleContext): string => {
  const value = resolveCarryOverValue(carryOver.value, context);
  if (value === undefined) return '(none)';
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'object' && value !== null && 'name' in value && typeof value.name === 'string') {
    return value.name;
  }
  return String(value);
};

// Formats a resolved value for the debug tooltip. A date reads as ISO, so it
// carries its time zone, and a value the template does not give reads as null,
// which the tooltip renders as the markup bound. Delete this with the rest of
// the form-rule debug UI.
// DEV-ONLY form-rule debug. The bounds a field's markup holds, which the form
// applies while the template gives none. A field passes the bound it has and
// leaves the other out.
export interface MarkupBounds {
  min?: number | Date;
  max?: number | Date;
}

// DEV-ONLY form-rule debug. Turns the bounds a caller passed into the three
// states the tooltip reads. A caller that passed no bounds states nothing, so
// this gives undefined. A caller that passed bounds and named neither this one
// states that the markup holds none, so this gives null. See MarkupFallback.
const statedFallback = (bounds: MarkupBounds | undefined, name: 'min' | 'max'): MarkupFallback =>
  bounds === undefined ? undefined : (bounds[name] ?? null);

// DEV-ONLY form-rule debug. True when something states a bound for the field,
// so the tooltip has one to report: a node in the template, or a bound the
// markup passes. A field that takes no bound at all, such as a select or a text
// box, states neither.
const boundsStated = (field: FieldRules | undefined, bounds: MarkupBounds | undefined): boolean =>
  field?.min !== undefined || field?.max !== undefined || bounds !== undefined;

const formatResolvedValue = (value: number | Date | undefined): string | null => {
  if (value === undefined) return null;
  return value instanceof Date ? value.toISOString() : String(value);
};

// DEV-ONLY form-rule debug. Explains one section's visibility rule. Both
// explainSection and explainSectionRule use this, so the two stay the same.
// They differ only in the debug-mode guard. Delete this function with the rest
// of the form-rule debug UI.
const explainSectionSubject = (
  template: ReportFormRuleTemplate | null,
  context: RuleContext,
  tabId: string,
  sectionId: string,
  fallbackLabel: string | undefined
): RuleExplanation => {
  const section = template?.tabs?.[tabId]?.sections?.[sectionId];
  return withSubject(
    explainRule(section?.isSectionVisible, context),
    `${tabId}.${sectionId}`,
    section?.label,
    fallbackLabel,
    context
  );
};

export const ReportStore = signalStore(
  { providedIn: 'root' },
  withDevtools('ReportStore'),
  withState(initialReportState),
  // DEV-ONLY form-rule debug. See withFormRuleDebugReducer.
  withFormRuleDebugReducer(),
  withFormRuleDebugEventHandlers(),
  withReportEventHandlers(),
  withReportReducer(),
  withComputed((store) => {
    const operationalStore = inject(OperationalStore);
    const positionAndWeatherStore = inject(PositionAndWeatherStore);
    const powerStore = inject(PowerStore);
    const cargoDetailsStore = inject(CargoDetailsStore);
    const consumptionsStore = inject(ConsumptionsStore);
    const bunkerStore = inject(BunkerStore);
    const stockStore = inject(StockStore);
    const vesselSpecStore = inject(VesselSpecStore);
    const authStore = inject(AuthStore);
    const vesselReportsStore = inject(VesselReportsStore);

    // DEV-ONLY: the admin role a rule reads. adminOverride forces the role
    // while debugging: 'db' uses the real role, 'internal_admin' and 'admin'
    // force that role, and 'none' forces no admin role. Delete this computed
    // and read authStore.isInternalAdmin() and authStore.isAdmin() directly to
    // remove the override.
    const effectiveAdminRole = computed<Exclude<AdminOverride, 'db'>>(() => {
      const override = store.adminOverride();
      if (override !== 'db') {
        return override;
      }
      if (authStore.isInternalAdmin()) {
        return 'internal_admin';
      }
      return authStore.isAdmin() ? 'admin' : 'none';
    });

    // The data every rule reads. One computed keeps the rule context in one
    // place, so each visibility method builds the same context.
    //
    // formState is keyed by tab rules key, so a reportFieldTruthy path reads
    // 'operational.voyageItinerary.arrivingAtEosp'. It holds every tab, because
    // a rule on one tab can read a field on another. This context therefore
    // changes as the user types, and every rule re-evaluates with it.
    const ruleContext = computed<RuleContext>(() => ({
      reportType: store.reportType(),
      previousReportType: vesselReportsStore.vm().lastSubmittedReportType ?? null,
      isInternalAdmin: effectiveAdminRole() === 'internal_admin',
      isAdmin: effectiveAdminRole() === 'admin',
      vesselSpec: vesselSpecStore.vesselSpec() ?? null,
      formState: {
        operational: operationalStore.formState(),
        positionAndWeather: positionAndWeatherStore.formState(),
        power: powerStore.formState(),
        cargoDetails: cargoDetailsStore.formState(),
        consumption: consumptionsStore.formState(),
        bunker: bunkerStore.formState(),
        stock: stockStore.formState(),
      },
      // The report before this one, which `carryOverField` reads. It holds
      // plain values, where the form state holds a field for each one, and the
      // store holds an empty object until one loads.
      carryOverReport: store.carryOverReport(),
      // `tabVisible` and `sectionVisible` read the visibility rules from it.
      template: store.formRuleTemplate(),
    }));

    const isReportReadyForInit = computed(() => {
      if (store.loading()) return false;
      const carryOverReport = store.carryOverReport();
      return (
        carryOverReport !== null &&
        Object.keys(carryOverReport).length !== 0 &&
        store.formFieldRules() !== null &&
        store.formRuleTemplate() !== null
      );
    });

    const isReportReadOnly = computed(() => isReadOnlyReportStatus(store.reportStatus()));

    const availableFuelGrades = computed(() =>
      getAvailableFuelGrades(store.carryOverReport(), vesselSpecStore.vesselSpec())
    );
    const availableFuelGradeOptions = computed(() => getAvailableFuelGradeOptions(availableFuelGrades()));
    const tankOptions = computed(() => getTankOptions(vesselSpecStore.vesselSpec()));

    const isPortReport = computed(() => store.reportType() === ReportType.Port);
    // True when the template shows the tab. The rule fails open, as
    // isTabVisible does, and the tab strip reads the same rule.
    const isTabShown = (rulesKey: ReportTabRulesKey): boolean => {
      const rule = store.formRuleTemplate()?.tabs?.[rulesKey]?.isTabVisible;
      return rule ? evaluate(rule, ruleContext()) : true;
    };

    // The template rule, not the vessel spec, so that Shore Power follows the
    // tab strip. When the strip hides the Power tab, Shore Power moves to the
    // Operational tab.
    const isPowerTabVisible = computed(() => isTabShown('power'));
    const shouldShowShorePowerOnOperationalTab = computed(() => {
      if (!isPortReport()) return false;
      if (!isPowerTabVisible()) return true;

      const isMachineryRhCounterShownByDefault = !!store.formFieldRules()?.['power']?.['machineryRunningHours'];
      return !powerStore.vm().isDieselElectricPropulsion && !isMachineryRhCounterShownByDefault;
    });

    // Shore power shows on the Power tab or on the Operational tab, and only on
    // a Port report. The Power store holds its fields but cannot tell where the
    // section shows, so this store decides. A hidden field keeps the status it
    // got on load, and nothing on screen can clear it.
    //
    // Each tab holds its own shorePower section rule, and the section reads
    // the rule of the tab that draws it.
    const isShorePowerShown = computed(() => {
      if (!isPortReport()) return false;

      const tabId: ReportTabRulesKey = shouldShowShorePowerOnOperationalTab() ? 'operational' : 'power';
      const rule = store.formRuleTemplate()?.tabs?.[tabId]?.sections?.['shorePower']?.isSectionVisible;
      return rule ? evaluate(rule, ruleContext()) : true;
    });

    // A tab that the template hides counts as valid. Its fields keep the
    // status they got on load, and the user cannot open the tab to clear it.
    // An example is the required cells of a cargo row on a Sea report.
    const tabValidityMap = computed<Record<ReportTabRoute, boolean>>(() => {
      const validity: Record<ReportTabRoute, boolean> = {
        operational: operationalStore.vm().isOperationalValid ?? false,
        'position-and-weather': positionAndWeatherStore.vm().isPositionAndWeatherValid,
        power:
          powerStore.vm().isPowerValidExceptShorePower && (!isShorePowerShown() || powerStore.vm().isShorePowerValid),
        'cargo-details': cargoDetailsStore.vm().isCargoDetailsValid,
        // The Bunker tab draws a tank table, and the Stock store holds its rows.
        bunker:
          bunkerStore.vm().isBunkerValid && areTankFieldsValid(stockStore.formState()['tanks'], BUNKER_TANK_FIELDS),
        consumptions: consumptionsStore.vm().isConsumptionsValid,
        stock: stockStore.vm().isStockValid,
      };
      return Object.fromEntries(
        REPORT_TABS.map(({ route, rulesKey }) => [route, validity[route] || !isTabShown(rulesKey)])
      ) as Record<ReportTabRoute, boolean>;
    });

    // Gates Submit: every tab must be valid. Derived from the per-tab map so a
    // change in one tab only recomputes this flag, not the whole report.
    const isReportValid = computed(() => Object.values(tabValidityMap()).every(Boolean));

    return {
      tabValidityMap,
      isReportValid,
      isReportReadyForInit,
      isReportReadOnly,
      availableFuelGrades,
      availableFuelGradeOptions,
      tankOptions,
      isPortReport,
      shouldShowShorePowerOnOperationalTab,
      ruleContext,
      effectiveAdminRole,
    };
  }),
  withMethods((store) => ({
    isTabValid(tabId: ReportTabStripRoute): boolean {
      // Report History is in the strip but has no form state, so no entry here.
      const validity: Partial<Record<ReportTabStripRoute, boolean>> = store.tabValidityMap();
      return validity[tabId] ?? false;
    },
    isSectionVisible(tabId: ReportTabRulesKey, sectionId: string): boolean {
      // Fail open: a missing template or a missing rule never hides a section.
      const rule = store.formRuleTemplate()?.tabs?.[tabId]?.sections?.[sectionId]?.isSectionVisible;
      return rule ? evaluate(rule, store.ruleContext()) : true;
    },
    isTabVisible(tabId: ReportTabRulesKey): boolean {
      // Fail open: a missing template or a missing rule never hides a tab.
      const rule = store.formRuleTemplate()?.tabs?.[tabId]?.isTabVisible;
      return rule ? evaluate(rule, store.ruleContext()) : true;
    },
    // A table of a section, for a section that draws more than one. It answers
    // in the same way as its section: fail open, so a missing table entry draws
    // the table.
    isTableVisible(tabId: ReportTabRulesKey, sectionId: string, tableId: string): boolean {
      const rule = store.formRuleTemplate()?.tabs?.[tabId]?.sections?.[sectionId]?.tables?.[tableId]?.isTableVisible;
      return rule ? evaluate(rule, store.ruleContext()) : true;
    },
    getTableLabel(tabId: ReportTabRulesKey, sectionId: string, tableId: string, fallback: string): string {
      const table = store.formRuleTemplate()?.tabs?.[tabId]?.sections?.[sectionId]?.tables?.[tableId];
      return resolveLabel(table?.label, store.ruleContext()) ?? fallback;
    },
    // fieldId is a plain column id, or 'tableId.columnId' for a column that
    // belongs to one table of a section that draws several. Every field method
    // below takes the same two shapes.
    isFieldVisible(tabId: ReportTabRulesKey, sectionId: string, fieldId: string): boolean {
      // Fail open: a missing template or a missing rule never hides a field.
      const rule = findField(store.formRuleTemplate(), tabId, sectionId, fieldId)?.isFieldVisible;
      return rule ? evaluate(rule, store.ruleContext()) : true;
    },
    // Fail closed: a missing template or a missing rule makes the field
    // optional. This is the mirror of the visibility rules, which fail open. A
    // form that shows one field too many still submits. A form that demands a
    // value the user has no way to know about does not.
    //
    // A field the user cannot see is never required, whether its own rule, its
    // section rule or its tab rule hides it. The engine applies this, so a
    // template never repeats a visibility rule inside a required rule.
    isFieldRequired(tabId: ReportTabRulesKey, sectionId: string, fieldId: string): boolean {
      if (!isFieldOnScreen(store.formRuleTemplate(), store.ruleContext(), tabId, sectionId, fieldId)) {
        return false;
      }
      const rule = findField(store.formRuleTemplate(), tabId, sectionId, fieldId)?.isFieldRequired;
      return rule ? evaluate(rule, store.ruleContext()) : false;
    },
    // Fail open: a missing template or a missing rule lets the user type. This
    // follows the visibility rules, not the required rule. A lock the template
    // loses shows a value the user can change, and the system writes most of
    // these values back. A lock the template invents on a field the user must
    // fill stops the report.
    //
    // There is no on-screen check here, as isFieldRequired holds. A field the
    // user cannot see has no input to lock, so the answer never reaches anyone.
    isFieldReadOnly(tabId: ReportTabRulesKey, sectionId: string, fieldId: string): boolean {
      const rule = findField(store.formRuleTemplate(), tabId, sectionId, fieldId)?.isFieldReadOnly;
      return rule ? evaluate(rule, store.ruleContext()) : false;
    },
    // The second lock. It greys the field out and takes it out of the form,
    // where isFieldReadOnly shows the value and sends it.
    //
    // It answers in the same way as isFieldReadOnly: fail open, and no
    // on-screen check. The two are separate rules, and a field can take either
    // one on its own.
    isFieldDisabled(tabId: ReportTabRulesKey, sectionId: string, fieldId: string): boolean {
      const rule = findField(store.formRuleTemplate(), tabId, sectionId, fieldId)?.isFieldDisabled;
      return rule ? evaluate(rule, store.ruleContext()) : false;
    },
    // The Field Warnings of a field. A warning never stops the save or the
    // submit, so nothing here touches the field status. The input shows the
    // expected message only while its own value is empty.
    getFieldWarnings(tabId: ReportTabRulesKey, sectionId: string, fieldId: string): FieldWarnings {
      return resolveFieldWarnings(store.formRuleTemplate(), store.ruleContext(), tabId, sectionId, fieldId);
    },
    // The three label getters resolve the template label and fall back to the
    // text the markup holds. They use `??`, not `||`: an empty label in the
    // template is an instruction to show no label text, so only a missing label
    // falls back. resolveLabel returns undefined for a label the template does
    // not give, including a case list where no case is true and there is no
    // otherwise. See TemplateLabel in the form-rule template types.
    getTabLabel(tabId: ReportTabRulesKey, fallback: string): string {
      return resolveLabel(store.formRuleTemplate()?.tabs?.[tabId]?.label, store.ruleContext()) ?? fallback;
    },
    getSectionLabel(tabId: ReportTabRulesKey, sectionId: string, fallback: string): string {
      const section = store.formRuleTemplate()?.tabs?.[tabId]?.sections?.[sectionId];
      return resolveLabel(section?.label, store.ruleContext()) ?? fallback;
    },
    getFieldLabel(tabId: ReportTabRulesKey, sectionId: string, fieldId: string, fallback: string): string {
      const field = findField(store.formRuleTemplate(), tabId, sectionId, fieldId);
      return resolveLabel(field?.label, store.ruleContext()) ?? fallback;
    },
    // The lowest value a field accepts. It returns undefined when the template
    // holds no node, so the caller falls back to the bound in its own markup,
    // in the same way the label getters fall back to the text in the markup.
    // A number field and a date field both ask this, and each one takes the
    // type it can apply.
    getFieldMin(tabId: ReportTabRulesKey, sectionId: string, fieldId: string): number | Date | undefined {
      return resolveValue(findField(store.formRuleTemplate(), tabId, sectionId, fieldId)?.min, store.ruleContext());
    },
    // The highest value a field accepts. It mirrors getFieldMin.
    getFieldMax(tabId: ReportTabRulesKey, sectionId: string, fieldId: string): number | Date | undefined {
      return resolveValue(findField(store.formRuleTemplate(), tabId, sectionId, fieldId)?.max, store.ruleContext());
    },
    // Whether the field draws a button that carries the previous value.
    //
    // Fail closed, as the required rule does, and unlike every other rule
    // here. A missing template and a missing entry both draw no button,
    // because this rule writes: a button nobody asked for puts a value over
    // what the user typed.
    //
    // The two locks do not answer here. A disabled field and a read-only field
    // each draw the button and grey it, so a reader sees the field would carry
    // if the lock were off. The section component composes that from the two
    // lock methods it already calls.
    canCarryOverOnRequest(tabId: ReportTabRulesKey, sectionId: string, fieldId: string): boolean {
      return findField(store.formRuleTemplate(), tabId, sectionId, fieldId)?.carryOver?.onRequest === true;
    },
    // The value the carry-over writes. It reads the second reader over the
    // value tree, so it gives whatever the control holds: a string, a boolean
    // and a select option as well as a number and a date.
    //
    // It answers on its own, so a field that carries on creation alone still
    // reports what it would write. The debug UI shows both, and the two facts
    // are separate: what the template would write, and whether the user can
    // ask for it.
    getCarryOverValue(tabId: ReportTabRulesKey, sectionId: string, fieldId: string): unknown {
      const carryOver = findField(store.formRuleTemplate(), tabId, sectionId, fieldId)?.carryOver;
      return resolveCarryOverValue(carryOver?.value, store.ruleContext());
    },
    // Every value the template carries into a new report, ready to write. It
    // is the `onCreate` half, where `getCarryOverValue` above is the
    // `onRequest` half: that one answers for one field, because the user
    // presses a button beside it, and this one answers for every field at
    // once, because a new report starts once.
    //
    // The caller reads it once and dispatches the list, so every entry sees
    // one context and no entry reads another entry's write. See
    // `collectCarryOverWrites`.
    //
    // The two locks say nothing here. They state what the user can change,
    // and a field the system fills is the field that most wants a
    // creation-time carry-over.
    carryOverWritesOnCreate(): CarryOverWrite[] {
      return collectCarryOverWrites(store.formRuleTemplate(), store.ruleContext());
    },
    // DEV-ONLY form-rule debug. Delete this block, withFormRuleDebugReducer and
    // withFormRuleDebugEventHandlers to remove the form-rule debug UI.
    // The templates the form applies now: the override while one is set, and
    // the list the server reported otherwise. An empty list means the server
    // sends no appliedTemplates yet, so the debug UI says so rather than
    // claiming the form applies nothing.
    appliedTemplates(): string[] {
      return store.templateOverride() ?? store.formRuleTemplate()?.appliedTemplates ?? [];
    },
    explainTab(tabId: ReportTabRulesKey, fallbackLabel?: string) {
      const tab = store.formRuleTemplate()?.tabs?.[tabId];
      const context = store.ruleContext();
      return withSubject(explainRule(tab?.isTabVisible, context), tabId, tab?.label, fallbackLabel, context);
    },
    explainSection(tabId: ReportTabRulesKey, sectionId: string, fallbackLabel?: string) {
      return explainSectionSubject(store.formRuleTemplate(), store.ruleContext(), tabId, sectionId, fallbackLabel);
    },
    // fallbackBounds is the pair the markup holds, the same numbers the markup
    // gives getFieldMin and getFieldMax. The tooltip reads them no other way,
    // so a caller that knows a bound passes it, and a field with no markup
    // bound passes nothing.
    explainField(
      tabId: ReportTabRulesKey,
      sectionId: string,
      fieldId: string,
      fallbackLabel?: string,
      fallbackBounds?: MarkupBounds
    ) {
      const field = findField(store.formRuleTemplate(), tabId, sectionId, fieldId);
      const context = store.ruleContext();
      // A field holds four rules. The tooltip shows the visibility rule first,
      // because it answers why the field is on screen at all, then the required
      // rule and the two locks below it.
      //
      // A field the user cannot see is never required, so the tooltip says that
      // in place of the rule. The rule below would otherwise report an answer
      // the form does not use.
      const onScreen = isFieldOnScreen(store.formRuleTemplate(), context, tabId, sectionId, fieldId);
      const required = onScreen
        ? explainRule(field?.isFieldRequired, context, MISSING_REQUIRED)
        : blockedExplanation('the field does not show, so nothing requires it');

      return withSubject(
        explainRule(field?.isFieldVisible, context),
        `${tabId}.${sectionId}.${fieldId}`,
        field?.label,
        fallbackLabel,
        context,
        [
          { name: 'isFieldRequired', explanation: required },
          // The read-only rule needs no on-screen check. It fails open, and a
          // field the user cannot see has no input to lock.
          { name: 'isFieldReadOnly', explanation: explainRule(field?.isFieldReadOnly, context, MISSING_READ_ONLY) },
          // The disabled rule sits below its twin, because a reader compares
          // the two. It needs no on-screen check for the same reason.
          { name: 'isFieldDisabled', explanation: explainRule(field?.isFieldDisabled, context, MISSING_DISABLED) },
          // The Field Warnings sit below the locks, because a lock decides
          // whether a warning shows at all.
          ...explainFieldWarnings(store.formRuleTemplate(), context, tabId, sectionId, fieldId),
        ],
        // The value nodes sit below the rules, because a rule answers whether
        // the field is there at all and a value node only shapes what it takes.
        //
        // A field shows them only where something states a bound: a node in the
        // template, or a bound the markup passes. A select and a text box take
        // no bound, and two empty blocks on every such field bury the rules
        // that do apply. The blocks come back with the first node or the first
        // markup bound.
        //
        // The two show together or not at all. A field that holds one bound has
        // something to say about the other, and one block without its twin
        // reads as a bug in the tooltip.
        !boundsStated(field, fallbackBounds)
          ? []
          : [
              {
                name: 'min',
                value: formatResolvedValue(resolveValue(field?.min, context)),
                fallback: formatResolvedValue(fallbackBounds?.min),
                fallbackStated: fallbackBounds !== undefined,
                lines: explainValue(field?.min, context, statedFallback(fallbackBounds, 'min')),
              },
              {
                name: 'max',
                value: formatResolvedValue(resolveValue(field?.max, context)),
                fallback: formatResolvedValue(fallbackBounds?.max),
                fallbackStated: fallbackBounds !== undefined,
                lines: explainValue(field?.max, context, statedFallback(fallbackBounds, 'max')),
              },
            ],
        // The carry-over block sits below the bounds. It is the one rule that
        // writes, and it shows on every field, because carry-over applies to
        // every field.
        explainCarryOver(field?.carryOver, context, {
          readOnly: evaluateOr(field?.isFieldReadOnly, context, false),
          disabled: evaluateOr(field?.isFieldDisabled, context, false),
        })
      );
    },
    // showSectionInDebug keeps a rule-hidden section in the view while debug
    // mode is on, so the debug UI can show why the section hides. It matches
    // isSectionVisible while debug mode is off.
    showSectionInDebug(tabId: ReportTabRulesKey, sectionId: string): boolean {
      if (store.formDebugMode()) {
        return true;
      }
      const rule = store.formRuleTemplate()?.tabs?.[tabId]?.sections?.[sectionId]?.isSectionVisible;
      return rule ? evaluate(rule, store.ruleContext()) : true;
    },
    // explainSectionRule feeds the FormRuleDebugDirective. It returns null
    // while debug mode is off, so the directive stays inert.
    explainSectionRule(tabId: ReportTabRulesKey, sectionId: string, fallbackLabel?: string): RuleExplanation | null {
      if (!store.formDebugMode()) {
        return null;
      }
      return explainSectionSubject(store.formRuleTemplate(), store.ruleContext(), tabId, sectionId, fallbackLabel);
    },
    // The table twins of the two section helpers above. A table is a section
    // inside a section, so it debugs in the same way.
    showTableInDebug(tabId: ReportTabRulesKey, sectionId: string, tableId: string): boolean {
      if (store.formDebugMode()) {
        return true;
      }
      const rule = store.formRuleTemplate()?.tabs?.[tabId]?.sections?.[sectionId]?.tables?.[tableId]?.isTableVisible;
      return rule ? evaluate(rule, store.ruleContext()) : true;
    },
    explainTableRule(
      tabId: ReportTabRulesKey,
      sectionId: string,
      tableId: string,
      fallbackLabel?: string
    ): RuleExplanation | null {
      if (!store.formDebugMode()) {
        return null;
      }
      const table = store.formRuleTemplate()?.tabs?.[tabId]?.sections?.[sectionId]?.tables?.[tableId];
      const context = store.ruleContext();
      return withSubject(
        explainRule(table?.isTableVisible, context),
        `${tabId}.${sectionId}.${tableId}`,
        table?.label,
        fallbackLabel,
        context
      );
    },
  })),
  // Must stay below withComputed and withMethods: it reads ruleContext through
  // carryOverWritesOnCreate. See with-carry-over-on-create.ts.
  withCarryOverOnCreate(),
  withHooks({
    /**
     * Clears a stored INVALID that the form asks nothing about: a field the
     * template hides, or a field the template does not require and the user has
     * not filled in.
     *
     * A tab counts every field it finds, and it reads VALID and DISABLED alike
     * as valid. The status a field holds comes from its form control, except
     * before the tab renders, where `validateField` stamps it from the
     * form-field-rules payload. The payload and the template disagree, so a
     * field can hold an INVALID that no rule and no control agrees with, and
     * the tab then reports invalid with nothing on screen to explain why.
     *
     * This store is the one place that holds both the template and every tab's
     * form state, so it is the one place that can answer this. It writes the
     * status each tab already reads, which leaves the tab-specific validity
     * rules alone.
     *
     * It settles in one pass: the write leaves the field VALID or DISABLED, and
     * neither is INVALID, so the next run finds nothing.
     */
    onInit(store) {
      const dispatcher = inject(Dispatcher);

      effect(() => {
        const context = store.ruleContext();
        const stale = findStaleInvalidFields(
          store.formRuleTemplate(),
          context,
          context.formState as Partial<Record<ReportTabRulesKey, FormState>>
        );

        for (const { tabId, sectionId, fieldId, status } of stale) {
          dispatcher.dispatch(FORM_STORE_EVENTS[tabId].updateFormStateStatus({ sectionId, fieldId, value: status }));
        }
      });

      // Marks each value outside its Field Bound INVALID, also on a tab that
      // has not rendered. See findBoundStatuses.
      let marked: ReadonlySet<string> = new Set();
      effect(() => {
        const context = store.ruleContext();
        const result = findBoundStatuses(
          store.formRuleTemplate(),
          context,
          context.formState as Partial<Record<ReportTabRulesKey, FormState>>,
          marked
        );
        marked = result.marked;

        for (const { tabId, sectionId, fieldId, status } of result.statuses) {
          dispatcher.dispatch(FORM_STORE_EVENTS[tabId].updateFormStateStatus({ sectionId, fieldId, value: status }));
        }
      });

      // Sets the status of each Calculated Value from the template. A
      // Calculated Value changes when an input on another tab changes, so its
      // control is often not rendered to give the status. The tab stores write
      // the values. See withCalculatedValues.
      const calculatedValueStores = {
        operational: inject(OperationalStore),
        positionAndWeather: inject(PositionAndWeatherStore),
        power: inject(PowerStore),
        consumption: inject(ConsumptionsStore),
      };
      let judged: ReadonlyMap<string, JudgedValue> = new Map();

      // The stores live for the whole session, but init stamps each status
      // again for a new report. Forget the last run of a tab at its init, so
      // a value that is equal in the last report cannot keep that stamp.
      const events = inject(Events);
      for (const tabId of Object.keys(calculatedValueStores) as (keyof typeof calculatedValueStores)[]) {
        events
          .on(FORM_STORE_EVENTS[tabId].initialiseFormState)
          .pipe(takeUntilDestroyed())
          .subscribe(() => {
            judged = new Map([...judged].filter(([key]) => !key.startsWith(`${tabId}.`)));
          });
      }

      // The Power store holds shore power, but cannot tell which tab draws
      // it. A shore power value takes the rules of that tab.
      const withShorePowerRulesTab = (calculatedValues: CalculatedValue[]): CalculatedValue[] => {
        if (!store.shouldShowShorePowerOnOperationalTab()) return calculatedValues;

        return calculatedValues.map((calculated) =>
          calculated.sectionId === 'shorePower'
            ? { ...calculated, rule: { sectionId: 'shorePower', fieldId: calculated.fieldId, tabId: 'operational' } }
            : calculated
        );
      };

      effect(() => {
        const template = store.formRuleTemplate();
        if (!template) return;

        const sources = Object.fromEntries(
          Object.entries(calculatedValueStores).map(([tabId, tabStore]) => [
            tabId,
            {
              formState: tabStore.formState(),
              calculatedValues:
                tabId === 'power' ? withShorePowerRulesTab(tabStore.calculatedValues()) : tabStore.calculatedValues(),
            },
          ])
        );
        const result = findCalculatedValueStatuses(template, store.ruleContext(), sources, judged);
        judged = result.judged;

        for (const { tabId, sectionId, fieldId, index, property, status } of result.statuses) {
          dispatcher.dispatch(
            FORM_STORE_EVENTS[tabId].updateFormStateStatus({ sectionId, fieldId, index, property, value: status })
          );
        }
      });
    },
  })
);
